import React, { useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { isURL } from 'validator'

import {
  Box,
  Button,
  Drawer,
  Flex,
  Header,
  Icon,
  IconButton,
  Link,
  Separator,
  Skeleton,
  styled,
  Text,
  UserAvatar,
} from '@driftt/dds'
import { faFilePdf } from '@driftt/dds.icons/light/faFilePdf'
import { faGlobe } from '@driftt/dds.icons/light/faGlobe'
import { faArrowLeft } from '@driftt/dds.icons/solid/faArrowLeft'
import { faArrowRight } from '@driftt/dds.icons/solid/faArrowRight'

import { isNilOrEmpty } from 'utils/ramda'

const StyledText = styled(Text, {
  alignSelf: 'center',
})

const StyledFlex = styled(Flex, {
  padding: '0 $2',
})
const StyledLink = styled(Link, {
  display: 'flex',
  gap: '$half',
  width: '100%',
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordWrap: 'break-word',
})

import { SiteConciergeAPI } from 'api'

import { SiteConciergeSearchTableMetricsRow } from '../types'

export interface SearchMessage {
  id: string
  createdAt: string
  updatedAt: string
  sessionId: string
  endUserId: number
  prompt: string
  response: string
  rating: number | null
  references: Array<{ url: string; title: string }>
  isGeneratedQuestion: boolean
}

interface DetailsDrawerProps {
  isLoadingTableData: boolean
  searches: SiteConciergeSearchTableMetricsRow | null
  onClose: () => void
  onPrevItem: () => void
  onNextItem: () => void
}

export const DetailsDrawer = ({
  isLoadingTableData,
  searches,
  onClose,
  onPrevItem,
  onNextItem,
}: DetailsDrawerProps) => {
  const { messageId } = searches || {}

  const { data, isLoading } = useQuery<SearchMessage[]>({
    queryKey: ['search_messages', messageId],
    queryFn: () =>
      SiteConciergeAPI.get(`/search/messages/${messageId}`).then(
        ({ data }) => data
      ),
    enabled: !isNilOrEmpty(messageId),
  })

  const scrollToMessage = (messageId: string) => {
    const message = document.getElementById(messageId)
    if (message) {
      message.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    if (messageId) {
      scrollToMessage(messageId)
    }
  }, [messageId, isLoading])

  return (
    <Drawer.Root open={!isNilOrEmpty(messageId)} onClose={onClose}>
      <Drawer.Dialog size="small">
        <Drawer.Title
          prefix={
            <Flex direction="row" gap={0} fullWidth alignItems="center">
              <IconButton
                title=""
                icon={faArrowLeft}
                onClick={onPrevItem}
                disabled={isLoadingTableData}
              />
              <IconButton
                title=""
                icon={faArrowRight}
                onClick={onNextItem}
                disabled={isLoadingTableData}
              />
            </Flex>
          }
          title=""
        />
        <Skeleton isLoading={isLoading} height={'100%'}>
          <Drawer.Content
            as={Flex}
            direction="column"
            fullHeight
            css={{
              padding: '$0',
            }}
            gap={2}
          >
            <Flex direction="column" fullWidth>
              <Separator />
              <StyledFlex alignItems="center">
                <UserAvatar size="small" name={searches?.visitor} />
                <Text>{searches?.visitor}</Text>
              </StyledFlex>
              <Separator />
            </Flex>
            <Flex direction="column" gap={0}>
              <StyledText variant="metadata">
                {moment(searches?.date).format('dddd, MMMM D, YYYY')}
              </StyledText>
              {data?.map((messages, index) => (
                <Box
                  key={index}
                  id={messages.id}
                  fullWidth
                  css={{
                    borderLeft:
                      messageId === messages.id
                        ? '3px solid $blue6'
                        : '3px solid transparent',
                  }}
                >
                  <Flex
                    direction="column"
                    css={{
                      padding: '$2',
                    }}
                  >
                    <Header variant="h3">{messages.prompt}</Header>
                    <Text>{messages.response}</Text>
                    {!isNilOrEmpty(messages.references) && (
                      <Flex direction="column" fullWidth>
                        <Text variant="bodyBold">Sources:</Text>
                        <Flex direction="column" gap="half">
                          {messages.references?.map((reference, index) => (
                            <StyledLink
                              variant="inline"
                              key={index}
                              size="small"
                              href={reference.url}
                              external
                            >
                              <Icon
                                icon={
                                  isURL(reference.url) ? faGlobe : faFilePdf
                                }
                              />
                              {reference.title}
                            </StyledLink>
                          ))}
                        </Flex>
                      </Flex>
                    )}
                    <Text variant="metadata">
                      {moment(messages.createdAt).format('h:mm A')}
                    </Text>
                  </Flex>
                  {index !== data?.length - 1 && <Separator />}
                </Box>
              ))}
            </Flex>
          </Drawer.Content>
        </Skeleton>
        <Drawer.Actions>
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
        </Drawer.Actions>
      </Drawer.Dialog>
    </Drawer.Root>
  )
}
